import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Tabs, Tab, Snackbar, Alert } from '@mui/material';
import CrearCampana from './CrearCampana';
import CargarTemplateHtml from './CargarTemplateHtml';

const ESTADO_STYLES = {
  borrador:  'bg-gray-100 text-gray-700',
  activa:    'bg-green-100 text-green-700',
  pausada:   'bg-yellow-100 text-yellow-700',
  finalizada:'bg-red-100 text-red-700',
  expirada:  'bg-orange-100 text-orange-700',
  archivada: 'bg-gray-200 text-gray-500',
};

const ESTADO_LABEL = {
  borrador:  'Borrador',
  activa:    'Activa',
  pausada:   'Pausada',
  finalizada:'Finalizada',
  expirada:  'Expirada',
  archivada: 'Archivada',
};

const DESTINATARIO_STYLES = {
  pendiente: 'bg-yellow-100 text-yellow-700',
  enviado:   'bg-blue-100 text-blue-700',
  canjeado:  'bg-green-100 text-green-700',
  expirado:  'bg-red-100 text-red-700',
};

const DESTINATARIO_LABEL = {
  pendiente: 'Pendiente',
  enviado:   'Enviado',
  canjeado:  'Canjeado',
  expirado:  'Expirado',
};

const getDestinatarioDisplay = (d) => {
  if (d.estado === 'pendiente' && d.fecha_envio) return 'enviado';
  return d.estado;
};

const COTIZACION_STYLES = {
  pendiente: 'bg-yellow-100 text-yellow-700',
  aprobado:  'bg-green-100 text-green-700',
  rechazado: 'bg-red-100 text-red-700',
};

const FILTROS_DEST = [
  { key: 'pendiente', label: 'Pendientes' },
  { key: 'enviado',   label: 'Enviados' },
  { key: 'canjeado',  label: 'Canjeados' },
  { key: 'expirado',  label: 'Expirados' },
];

const PAGE_SIZE = 50;
const MAX_POR_ACCION = 500;

const DetalleCampana = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [campana, setCampana] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  const [destinatarios, setDestinatarios] = useState([]);
  const [cotizaciones, setCotizaciones] = useState([]);
  const [loadingTab, setLoadingTab] = useState(false);

  const [filtroDestinatario, setFiltroDestinatario] = useState('pendiente');
  const [paginaDestinatarios, setPaginaDestinatarios] = useState(1);
  const [totalDestinatarios, setTotalDestinatarios] = useState(0);
  const [countsDestinatarios, setCountsDestinatarios] = useState({ pendiente: 0, enviado: 0, canjeado: 0, expirado: 0 });

  const [modalEditar, setModalEditar] = useState(false);
  const [modalTemplate, setModalTemplate] = useState(false);
  const [accionando, setAccionando] = useState(false);
  const [accionError, setAccionError] = useState(null);

  const [emailsInput, setEmailsInput] = useState('');
  const [cargandoDestinatarios, setCargandoDestinatarios] = useState(false);
  const [cargaResult, setCargaResult] = useState(null);
  const [csvInfo, setCsvInfo] = useState(null);
  const csvInputRef = useRef(null);

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [enviando, setEnviando] = useState(false);
  const [confirmEnvio, setConfirmEnvio] = useState(false);

  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const token = () => localStorage.getItem('access_token');
  const showToast = (message, severity = 'success') => setToast({ open: true, message, severity });

  const fetchCampana = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/campanas/${id}/`,
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      setCampana(res.data);
    } catch {
      setError('No se pudo cargar la campaña.');
    } finally {
      setLoading(false);
    }
  };

  const fetchDestinatarios = async (filtro, page) => {
    setLoadingTab(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/campanas/${id}/recipients/?filtro=${filtro}&page=${page}`,
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      setDestinatarios(res.data.results);
      setTotalDestinatarios(res.data.total);
      setCountsDestinatarios(res.data.counts);
    } catch {}
    setLoadingTab(false);
  };

  const fetchCotizaciones = async () => {
    setLoadingTab(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/campanas/${id}/cotizaciones/`,
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      setCotizaciones(res.data);
    } catch {}
    setLoadingTab(false);
  };

  useEffect(() => { fetchCampana(); }, [id]);

  useEffect(() => {
    if (activeTab === 1) fetchDestinatarios(filtroDestinatario, paginaDestinatarios);
    if (activeTab === 2) fetchCotizaciones();
  }, [activeTab, filtroDestinatario, paginaDestinatarios]);

  const handleFiltroDestinatario = (nuevoFiltro) => {
    if (nuevoFiltro === filtroDestinatario) return;
    setFiltroDestinatario(nuevoFiltro);
    setPaginaDestinatarios(1);
    setSelectedIds(new Set());
  };

  const handlePageChange = (newPage) => {
    setPaginaDestinatarios(newPage);
    setSelectedIds(new Set());
  };

  const cambiarEstado = async (nuevoEstado) => {
    setAccionando(true);
    setAccionError(null);
    try {
      await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/api/campanas/${id}/`,
        { estado: nuevoEstado },
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      await fetchCampana();
    } catch (err) {
      const msg = err.response?.data?.error || 'Error al cambiar el estado de la campaña.';
      setAccionError(msg);
    } finally {
      setAccionando(false);
    }
  };

  const cargarDestinatarios = async () => {
    const emails = [...new Set(
      emailsInput
        .split(/[\r\n,;]+/)
        .map((e) => e.trim().toLowerCase())
        .filter(Boolean)
    )];
    if (emails.length === 0) return;
    setCargandoDestinatarios(true);
    setCargaResult(null);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/campanas/${id}/recipients/`,
        { emails },
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      setCargaResult(res.data);
      setEmailsInput('');
      setPaginaDestinatarios(1);
      fetchDestinatarios(filtroDestinatario, 1);
    } catch {
      showToast('Error al cargar destinatarios.', 'error');
    } finally {
      setCargandoDestinatarios(false);
    }
  };

  const exportarCSV = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/campanas/${id}/export-csv/`,
        { headers: { Authorization: `Bearer ${token()}` }, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `campana_${id}_urls.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      showToast('Error al exportar el CSV.', 'error');
    }
  };

  const sendEmails = async (recipientIds) => {
    setEnviando(true);
    setConfirmEnvio(false);
    try {
      const body = recipientIds ? { recipient_ids: recipientIds } : {};
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/campanas/${id}/send-emails/`,
        body,
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      const { enviados, errores, errores_detalle, pendientes_restantes } = res.data;
      const detalle = errores_detalle?.length
        ? ` — ${errores_detalle.map(e => e.email ? `${e.email}: ${e.error}` : e.error || JSON.stringify(e)).join('; ')}`
        : '';
      const restoMsg = pendientes_restantes > 0
        ? ` · Quedan ${pendientes_restantes} pendientes.`
        : ' · No quedan pendientes.';
      showToast(
        `${enviados} email${enviados !== 1 ? 's' : ''} enviado${enviados !== 1 ? 's' : ''}, ${errores} error${errores !== 1 ? 'es' : ''}${detalle}${restoMsg}`,
        errores > 0 ? 'warning' : 'success'
      );
      setSelectedIds(new Set());
      setFiltroDestinatario('pendiente');
      setPaginaDestinatarios(1);
      fetchDestinatarios('pendiente', 1);
    } catch (err) {
      const msg = err.response?.data?.error || 'Error al enviar emails.';
      showToast(msg, 'error');
    } finally {
      setEnviando(false);
    }
  };

  const toggleSelect = (recipientId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(recipientId)) next.delete(recipientId); else next.add(recipientId);
      return next;
    });
  };

  const toggleAll = () => {
    setSelectedIds(
      selectedIds.size === destinatarios.length
        ? new Set()
        : new Set(destinatarios.map((d) => d.id))
    );
  };

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const handleCsvImport = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result || '';
      const lines = text.split(/\r?\n/).filter((l) => l.trim());
      if (!lines.length) return;

      const firstLine = lines[0];
      const sep = (firstLine.match(/;/g) || []).length >= (firstLine.match(/,/g) || []).length ? ';' : ',';

      const strip = (s) => s.trim().replace(/^["']|["']$/g, '');
      const headers = firstLine.split(sep).map(strip).map((h) => h.toLowerCase());
      const emailColIdx = headers.findIndex((h) => ['email', 'correo', 'mail', 'e-mail'].includes(h));

      let emails = [];
      if (emailColIdx !== -1) {
        for (let i = 1; i < lines.length; i++) {
          const val = strip(lines[i].split(sep)[emailColIdx] || '');
          if (EMAIL_RE.test(val)) emails.push(val.toLowerCase());
        }
      } else {
        for (const line of lines) {
          for (const cell of line.split(sep)) {
            const val = strip(cell);
            if (EMAIL_RE.test(val)) emails.push(val.toLowerCase());
          }
        }
      }

      emails = [...new Set(emails)];
      setEmailsInput((prev) => {
        const existing = prev.trim();
        return existing ? `${existing}\n${emails.join('\n')}` : emails.join('\n');
      });
      setCsvInfo({
        count: emails.length,
        sep: sep === ';' ? 'punto y coma' : 'coma',
        columna: emailColIdx !== -1 ? headers[emailColIdx] : null,
      });
      setCargaResult(null);
    };
    reader.readAsText(file, 'UTF-8');
    e.target.value = '';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-400">Cargando campaña...</p>
      </div>
    );
  }

  if (error || !campana) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-red-500">{error || 'Campaña no encontrada.'}</p>
      </div>
    );
  }

  const canActivate = !!campana.countdown_url && !!campana.html_template;
  const totalPages = Math.ceil(totalDestinatarios / PAGE_SIZE);
  const cantAEnviar = Math.min(MAX_POR_ACCION, countsDestinatarios.pendiente);

  const activateBlockers = [
    !campana.countdown_url && 'URL de countdown',
    !campana.html_template && 'template HTML',
  ].filter(Boolean);

  const BotonesAccion = () => {
    if (campana.estado === 'borrador') return (
      <>
        <button onClick={() => setModalEditar(true)} className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm hover:bg-gray-50">
          Editar
        </button>
        <button
          onClick={() => setModalTemplate(true)}
          className="px-4 py-2 rounded-lg border text-sm font-medium"
          style={campana.html_template
            ? { borderColor: '#16a34a', color: '#15803d', backgroundColor: '#f0fdf4' }
            : { borderColor: '#f97316', color: '#ea580c', backgroundColor: '#fff7ed' }}
        >
          {campana.html_template ? 'Template cargado ✓' : 'Cargar template HTML'}
        </button>
        <div className="flex flex-col items-end gap-1">
          <button
            onClick={() => cambiarEstado('activa')}
            disabled={accionando || !canActivate}
            className="px-4 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-50"
            style={{ backgroundColor: '#FF743A' }}
          >
            Activar
          </button>
          {activateBlockers.length > 0 && (
            <p className="text-xs text-amber-600">Falta: {activateBlockers.join(' y ')}</p>
          )}
        </div>
      </>
    );
    if (campana.estado === 'activa') return (
      <>
        <button
          onClick={() => setModalTemplate(true)}
          className="px-4 py-2 rounded-lg border border-green-400 text-green-700 text-sm bg-green-50 hover:bg-green-100"
        >
          Template cargado ✓
        </button>
        <button onClick={() => cambiarEstado('pausada')} disabled={accionando} className="px-4 py-2 rounded-lg border border-yellow-400 text-yellow-700 text-sm hover:bg-yellow-50 disabled:opacity-50">
          Pausar
        </button>
        <button onClick={() => cambiarEstado('finalizada')} disabled={accionando} className="px-4 py-2 rounded-lg border border-red-400 text-red-600 text-sm hover:bg-red-50 disabled:opacity-50">
          Finalizar
        </button>
      </>
    );
    if (campana.estado === 'pausada') return (
      <>
        <button
          onClick={() => setModalTemplate(true)}
          className="px-4 py-2 rounded-lg border text-sm font-medium"
          style={campana.html_template
            ? { borderColor: '#16a34a', color: '#15803d', backgroundColor: '#f0fdf4' }
            : { borderColor: '#f97316', color: '#ea580c', backgroundColor: '#fff7ed' }}
        >
          {campana.html_template ? 'Template cargado ✓' : 'Cargar template HTML'}
        </button>
        <div className="flex flex-col items-end gap-1">
          <button
            onClick={() => cambiarEstado('activa')}
            disabled={accionando || !canActivate}
            className="px-4 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-50"
            style={{ backgroundColor: '#FF743A' }}
          >
            Reactivar
          </button>
          {activateBlockers.length > 0 && (
            <p className="text-xs text-amber-600">Falta: {activateBlockers.join(' y ')}</p>
          )}
        </div>
        <button onClick={() => cambiarEstado('finalizada')} disabled={accionando} className="px-4 py-2 rounded-lg border border-red-400 text-red-600 text-sm hover:bg-red-50 disabled:opacity-50">
          Finalizar
        </button>
      </>
    );
    if (campana.estado === 'expirada' || campana.estado === 'finalizada') return (
      <button onClick={() => cambiarEstado('archivada')} disabled={accionando} className="px-4 py-2 rounded-lg border border-gray-400 text-gray-600 text-sm hover:bg-gray-50 disabled:opacity-50">
        Archivar
      </button>
    );
    return null;
  };

  return (
    <div className="h-full flex flex-col p-2 md:p-4 overflow-hidden">
      <div className="flex-1 min-h-0 flex flex-col bg-white rounded-3xl p-4 md:p-6 overflow-hidden">

        {/* Header */}
        <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/marketing')} className="text-gray-400 hover:text-gray-700 text-lg font-bold" title="Volver a Marketing">
              ←
            </button>
            <div>
              <h1 className="text-xl md:text-2xl font-extrabold text-gray-900">{campana.nombre}</h1>
              <div className="flex items-center gap-2 mt-1">
                <span className="font-mono text-sm text-gray-500">{campana.codigo}</span>
                <span className={`px-2 py-0.5 rounded text-xs font-semibold ${ESTADO_STYLES[campana.estado] || 'bg-gray-100 text-gray-600'}`}>
                  {ESTADO_LABEL[campana.estado] || campana.estado}
                </span>
              </div>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap items-start">
            <BotonesAccion />
          </div>
        </div>

        {accionError && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
            {accionError}
          </div>
        )}

        {/* Tabs */}
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          TabIndicatorProps={{ style: { backgroundColor: '#FF743A' } }}
          sx={{
            borderBottom: '1px solid #e5e7eb',
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.9rem' },
            '& .Mui-selected': { color: '#FF743A !important' },
          }}
        >
          <Tab label="Resumen" />
          <Tab label="Destinatarios" />
          <Tab label="Cotizaciones" />
        </Tabs>

        <div className="flex-1 min-h-0 overflow-auto mt-4">

          {/* Pestaña 0: Resumen */}
          {activeTab === 0 && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="bg-orange-50 rounded-xl p-4">
                  <p className="text-xs text-gray-500 mb-1">Enviados</p>
                  <p className="text-2xl font-bold text-orange-600">{campana.total_enviados ?? '—'}</p>
                </div>
                <div className="bg-green-50 rounded-xl p-4">
                  <p className="text-xs text-gray-500 mb-1">Canjeados</p>
                  <p className="text-2xl font-bold text-green-600">{campana.total_canjeados ?? '—'}</p>
                </div>
                <div className="bg-blue-50 rounded-xl p-4 col-span-2 md:col-span-1">
                  <p className="text-xs text-gray-500 mb-1">Conversión</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {campana.conversion_pct != null ? `${Number(campana.conversion_pct).toFixed(1)}%` : '—'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Tipo de descuento</p>
                    <p className="font-medium text-gray-900">{campana.tipo_descuento === 'porcentaje' ? 'Porcentaje' : 'Monto fijo'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Valor del descuento</p>
                    <p className="font-medium text-gray-900">
                      {campana.tipo_descuento === 'porcentaje' ? `${campana.valor_descuento}%` : `$${Number(campana.valor_descuento).toLocaleString('es-CL')}`}
                    </p>
                  </div>
                  {campana.tope_maximo && (
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Tope máximo</p>
                      <p className="font-medium text-gray-900">${Number(campana.tope_maximo).toLocaleString('es-CL')}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Asunto del email</p>
                    <p className="font-medium text-gray-900">{campana.asunto || '—'}</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Fecha de inicio</p>
                    <p className="font-medium text-gray-900">{campana.fecha_inicio}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Duración del contador</p>
                    <p className="font-medium text-gray-900">{campana.duracion_contador_dias} días</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Fecha de creación</p>
                    <p className="font-medium text-gray-900">{new Date(campana.fecha_creacion).toLocaleDateString('es-CL')}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">URL countdown</p>
                    {campana.countdown_url ? (
                      <a href={campana.countdown_url} target="_blank" rel="noopener noreferrer" className="text-sm text-orange-600 underline break-all">
                        {campana.countdown_url}
                      </a>
                    ) : (
                      <p className="text-sm text-amber-600 font-medium">Sin URL de countdown — requerida para activar</p>
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Template HTML</p>
                    {campana.html_template ? (
                      <button
                        onClick={() => setModalTemplate(true)}
                        className="text-sm text-green-700 font-medium hover:underline"
                      >
                        Template cargado — ver / editar
                      </button>
                    ) : (
                      <button
                        onClick={() => setModalTemplate(true)}
                        className="text-sm text-amber-600 font-medium hover:underline"
                      >
                        Sin template — requerido para activar
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Pestaña 1: Destinatarios */}
          {activeTab === 1 && (
            <div className="space-y-4">

              {/* Cargar destinatarios */}
              <div className="border border-gray-200 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <h3 className="font-semibold text-gray-900">Cargar destinatarios</h3>
                  <button
                    onClick={() => csvInputRef.current?.click()}
                    className="px-3 py-1.5 rounded-lg border border-gray-300 text-gray-700 text-xs font-medium hover:bg-gray-50 flex items-center gap-1.5"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                    Importar CSV
                  </button>
                  <input
                    ref={csvInputRef}
                    type="file"
                    accept=".csv,.txt"
                    className="hidden"
                    onChange={handleCsvImport}
                  />
                </div>
                <textarea
                  value={emailsInput}
                  onChange={(e) => { setEmailsInput(e.target.value); setCsvInfo(null); }}
                  className="w-full border border-gray-300 rounded-lg p-3 text-sm h-28 resize-none focus:outline-none focus:ring-2 focus:ring-orange-300"
                  placeholder="Pega los emails aquí, uno por línea o separados por coma/punto y coma"
                />
                {csvInfo && (
                  <p className="text-sm text-blue-700 mt-2 bg-blue-50 border border-blue-200 p-2 rounded-lg">
                    CSV importado — <strong>{csvInfo.count}</strong> email{csvInfo.count !== 1 ? 's' : ''} encontrado{csvInfo.count !== 1 ? 's' : ''}
                    {csvInfo.columna ? <> en columna <strong>"{csvInfo.columna}"</strong></> : ' (sin cabecera de email)'}
                    {' '}· separador: <strong>{csvInfo.sep}</strong>
                  </p>
                )}
                {cargaResult && (
                  <p className="text-sm text-green-700 mt-2 bg-green-50 border border-green-200 p-2 rounded-lg">
                    Cargados exitosamente — Creados: <strong>{cargaResult.creados}</strong> · Duplicados: <strong>{cargaResult.duplicados}</strong>
                  </p>
                )}
                <div className="flex gap-2 mt-3 flex-wrap">
                  <button
                    onClick={cargarDestinatarios}
                    disabled={cargandoDestinatarios || !emailsInput.trim()}
                    className="px-4 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-50"
                    style={{ backgroundColor: '#FF743A' }}
                  >
                    {cargandoDestinatarios ? 'Cargando...' : 'Cargar destinatarios'}
                  </button>
                  <button onClick={exportarCSV} className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm hover:bg-gray-50">
                    Exportar CSV con URLs
                  </button>
                </div>
              </div>

              {/* Filtros de estado + botones de envío */}
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex gap-1 flex-wrap">
                  {FILTROS_DEST.map(({ key, label }) => (
                    <button
                      key={key}
                      onClick={() => handleFiltroDestinatario(key)}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                        filtroDestinatario === key
                          ? 'text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                      style={filtroDestinatario === key ? { backgroundColor: '#FF743A' } : {}}
                    >
                      {label} ({countsDestinatarios[key] ?? 0})
                    </button>
                  ))}
                </div>

                {campana.estado === 'activa' && filtroDestinatario === 'pendiente' && cantAEnviar > 0 && (
                  <div className="flex gap-2 flex-wrap">
                    {selectedIds.size > 0 && (
                      <button
                        onClick={() => sendEmails([...selectedIds])}
                        disabled={enviando}
                        className="px-4 py-2 rounded-lg border border-orange-400 text-orange-700 text-sm font-semibold hover:bg-orange-50 disabled:opacity-50"
                      >
                        Enviar seleccionados ({selectedIds.size})
                      </button>
                    )}
                    <button
                      onClick={() => setConfirmEnvio(true)}
                      disabled={enviando}
                      className="px-4 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-50"
                      style={{ backgroundColor: '#FF743A' }}
                    >
                      Enviar próximos {cantAEnviar} pendientes
                    </button>
                  </div>
                )}
              </div>

              {/* Tabla */}
              {loadingTab ? (
                <div className="flex flex-col items-center justify-center py-12 gap-3 text-gray-400">
                  <div className="w-8 h-8 border-4 border-gray-200 border-t-orange-400 rounded-full animate-spin" />
                  <p className="text-sm">Cargando...</p>
                </div>
              ) : (
                <>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-left">
                        {filtroDestinatario === 'pendiente' && campana.estado === 'activa' && (
                          <th className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={destinatarios.length > 0 && selectedIds.size === destinatarios.length}
                              onChange={toggleAll}
                              className="rounded"
                            />
                          </th>
                        )}
                        <th className="px-4 py-3 font-semibold text-gray-600">Email</th>
                        <th className="px-4 py-3 font-semibold text-gray-600">Estado</th>
                        <th className="px-4 py-3 font-semibold text-gray-600">Fecha expiración</th>
                        <th className="px-4 py-3 font-semibold text-gray-600">Fecha envío</th>
                        <th className="px-4 py-3 font-semibold text-gray-600">Fecha canje</th>
                      </tr>
                    </thead>
                    <tbody>
                      {destinatarios.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="text-center py-10 text-gray-400">
                            Sin destinatarios en este estado.
                          </td>
                        </tr>
                      ) : (
                        destinatarios.map((d) => (
                          <tr key={d.id} className="border-t border-gray-100">
                            {filtroDestinatario === 'pendiente' && campana.estado === 'activa' && (
                              <td className="px-4 py-3">
                                <input type="checkbox" checked={selectedIds.has(d.id)} onChange={() => toggleSelect(d.id)} className="rounded" />
                              </td>
                            )}
                            <td className="px-4 py-3 text-gray-800">{d.email}</td>
                            <td className="px-4 py-3">
                              {(() => { const key = getDestinatarioDisplay(d); return (
                                <span className={`px-2 py-0.5 rounded text-xs font-semibold ${DESTINATARIO_STYLES[key] || 'bg-gray-100 text-gray-600'}`}>
                                  {DESTINATARIO_LABEL[key] || key}
                                </span>
                              ); })()}
                            </td>
                            <td className="px-4 py-3 text-gray-500 text-xs">
                              {d.fecha_expiracion ? new Date(d.fecha_expiracion).toLocaleString('es-CL') : '—'}
                            </td>
                            <td className="px-4 py-3 text-gray-500 text-xs">
                              {d.fecha_envio ? new Date(d.fecha_envio).toLocaleString('es-CL') : '—'}
                            </td>
                            <td className="px-4 py-3 text-gray-500 text-xs">
                              {d.fecha_canje ? new Date(d.fecha_canje).toLocaleString('es-CL') : '—'}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>

                  {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-3 mt-4 text-sm text-gray-600">
                      <button
                        onClick={() => handlePageChange(paginaDestinatarios - 1)}
                        disabled={paginaDestinatarios <= 1}
                        className="px-3 py-1 rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-50"
                      >
                        ←
                      </button>
                      <span>Página {paginaDestinatarios} de {totalPages} · {totalDestinatarios} registros</span>
                      <button
                        onClick={() => handlePageChange(paginaDestinatarios + 1)}
                        disabled={paginaDestinatarios >= totalPages}
                        className="px-3 py-1 rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-50"
                      >
                        →
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Pestaña 2: Cotizaciones */}
          {activeTab === 2 && (
            loadingTab ? (
              <p className="text-center py-6 text-gray-400">Cargando cotizaciones...</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left">
                    <th className="px-4 py-3 font-semibold text-gray-600">ID</th>
                    <th className="px-4 py-3 font-semibold text-gray-600">Cliente</th>
                    <th className="px-4 py-3 font-semibold text-gray-600">Email</th>
                    <th className="px-4 py-3 font-semibold text-gray-600">Total</th>
                    <th className="px-4 py-3 font-semibold text-gray-600">Fecha</th>
                    <th className="px-4 py-3 font-semibold text-gray-600">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {cotizaciones.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-10 text-gray-400">
                        Sin cotizaciones vinculadas a esta campaña.
                      </td>
                    </tr>
                  ) : (
                    cotizaciones.map((c) => (
                      <tr key={c.id_cotizacion} className="border-t border-gray-100">
                        <td className="px-4 py-3 text-gray-600">#{c.id_cotizacion}</td>
                        <td className="px-4 py-3 font-medium text-gray-800">{c.solicitante_nombre}</td>
                        <td className="px-4 py-3 text-gray-500 text-xs">{c.solicitante_email}</td>
                        <td className="px-4 py-3 text-gray-700">${Number(c.total_cotizacion).toLocaleString('es-CL')}</td>
                        <td className="px-4 py-3 text-gray-500 text-xs">
                          {c.fecha_cotizacion ? new Date(c.fecha_cotizacion).toLocaleDateString('es-CL') : '—'}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded text-xs font-semibold ${COTIZACION_STYLES[c.estado] || 'bg-gray-100 text-gray-600'}`}>
                            {c.estado}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )
          )}
        </div>
      </div>

      {modalEditar && (
        <CrearCampana
          campana={campana}
          onClose={() => setModalEditar(false)}
          onSuccess={() => { setModalEditar(false); fetchCampana(); }}
        />
      )}

      {modalTemplate && (
        <CargarTemplateHtml
          campana={campana}
          onClose={() => setModalTemplate(false)}
          onSuccess={() => { setModalTemplate(false); fetchCampana(); }}
        />
      )}

      {/* Modal confirmación de envío */}
      {confirmEnvio && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full mx-4 shadow-xl">
            <h3 className="font-bold text-lg text-gray-900 mb-2">Confirmar envío</h3>
            <p className="text-gray-600 text-sm mb-1">
              Se enviarán <strong>{cantAEnviar}</strong> emails a destinatarios pendientes.
            </p>
            {countsDestinatarios.pendiente > MAX_POR_ACCION && (
              <p className="text-xs text-amber-600 mt-1">
                Quedarán <strong>{countsDestinatarios.pendiente - MAX_POR_ACCION}</strong> pendientes para el próximo envío.
              </p>
            )}
            <div className="flex gap-3 justify-end mt-6">
              <button
                onClick={() => setConfirmEnvio(false)}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={() => sendEmails(null)}
                disabled={enviando}
                className="px-4 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-50"
                style={{ backgroundColor: '#FF743A' }}
              >
                {enviando ? 'Enviando...' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast.severity} onClose={() => setToast((t) => ({ ...t, open: false }))} sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default DetalleCampana;